import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Brain, FileEdit, AlertTriangle, CheckCircle, 
  RefreshCcw, Info, Zap, Target, Clock, XCircle,
  TrendingUp, TrendingDown, Shield, ChevronRight,
  MessageSquare, GitBranch, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Autonomous Amendment Intelligence Admin Panel
 * Phase 1: Shadow Mode - Admin Only
 * 
 * AI-powered amendment analysis and recommendations
 */
const AmendmentIntelligencePanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Simulation inputs
  const [simCategory, setSimCategory] = useState('TIMELINE_EXTENSION');
  const [simPriceChange, setSimPriceChange] = useState(0);
  const [simExtensionDays, setSimExtensionDays] = useState(14);
  const [simDealStage, setSimDealStage] = useState('ACTIVE');
  const [simExistingAmendments, setSimExistingAmendments] = useState(0);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch overview
      const overviewRes = await fetch(`${API_URL}/api/admin/amendment-intelligence/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const overviewData = await overviewRes.json();
      if (overviewData.success) {
        setOverview(overviewData.data);
      }

      // Fetch patterns
      const patternsRes = await fetch(`${API_URL}/api/admin/amendment-intelligence/patterns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const patternsData = await patternsRes.json();
      if (patternsData.success) {
        setPatterns(patternsData.data);
      }
    } catch (err) {
      setError('Error connecting to Amendment Intelligence service');
    }
    setLoading(false);
  };

  const runSimulation = async () => {
    try {
      const params = new URLSearchParams({
        category: simCategory,
        price_change_pct: simPriceChange,
        extension_days: simExtensionDays,
        deal_stage: simDealStage,
        existing_amendments: simExistingAmendments
      });
      
      const res = await fetch(`${API_URL}/api/admin/amendment-intelligence/simulate?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSimulation(data.data);
      }
    } catch (err) {
      console.error('Simulation error:', err);
    }
  };

  const getRecommendationColor = (rec) => {
    switch (rec) {
      case 'APPROVE': return 'text-green-400 bg-green-600/20 border-green-600/30';
      case 'APPROVE_WITH_CONDITIONS': return 'text-blue-400 bg-blue-600/20 border-blue-600/30';
      case 'REQUEST_MODIFICATION': return 'text-yellow-400 bg-yellow-600/20 border-yellow-600/30';
      case 'ESCALATE': return 'text-orange-400 bg-orange-600/20 border-orange-600/30';
      case 'REJECT': return 'text-red-400 bg-red-600/20 border-red-600/30';
      default: return 'text-gray-400 bg-gray-600/20 border-gray-600/30';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'MINIMAL': return 'text-green-400';
      case 'LOW': return 'text-blue-400';
      case 'MODERATE': return 'text-yellow-400';
      case 'HIGH': return 'text-orange-400';
      case 'CRITICAL': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRecommendationIcon = (rec) => {
    switch (rec) {
      case 'APPROVE': return <CheckCircle className="w-4 h-4" />;
      case 'APPROVE_WITH_CONDITIONS': return <Shield className="w-4 h-4" />;
      case 'REQUEST_MODIFICATION': return <FileEdit className="w-4 h-4" />;
      case 'ESCALATE': return <AlertTriangle className="w-4 h-4" />;
      case 'REJECT': return <XCircle className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (cat) => {
    return cat?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <Brain className="w-8 h-8 animate-pulse mx-auto mb-4 text-rose-400" />
          <p className="text-muted-foreground">Loading Amendment Intelligence...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchData} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="amendment-intelligence-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-rose-400 flex items-center gap-2">
            <Brain className="w-6 h-6" />
            Amendment Intelligence
          </h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - AI Amendment Analysis & Recommendations</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Shadow Mode Banner */}
      <Card className="glass-card border-rose-600/30 bg-rose-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Zap className="w-5 h-5 text-rose-400" />
          <div>
            <p className="text-rose-400 font-medium">AI Amendment Co-Pilot Active (Shadow Mode)</p>
            <p className="text-xs text-muted-foreground">
              Analyzes proposed amendments, predicts risks, and recommends actions based on historical patterns and deal context.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'patterns', 'simulate'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className="capitalize"
            data-testid={`amendment-tab-${tab}`}
          >
            {tab}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-rose-400">{overview.total_amendments}</div>
                <p className="text-xs text-muted-foreground">Total Amendments</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-yellow-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-yellow-400">{overview.pending_amendments}</div>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-primary/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-primary">{overview.deals_with_amendments}</div>
                <p className="text-xs text-muted-foreground">Deals w/ Amendments</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-green-400">{overview.engine_status}</div>
                <p className="text-xs text-muted-foreground">Engine Status</p>
              </CardContent>
            </Card>
          </div>

          {/* Amendments by Category */}
          {overview.amendments_by_category && Object.keys(overview.amendments_by_category).length > 0 && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-rose-400" />
                  Amendments by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(overview.amendments_by_category).map(([cat, count]) => (
                    <div key={cat} className="p-4 rounded border border-white/10 text-center">
                      <div className="text-2xl font-display text-rose-400">{count}</div>
                      <p className="text-xs text-muted-foreground">{getCategoryLabel(cat)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Amendments */}
          {overview.recent_amendments?.length > 0 && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileEdit className="w-5 h-5 text-rose-400" />
                  Recent Amendments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview.recent_amendments.slice(0, 5).map((amend, idx) => (
                    <div key={idx} className="p-3 rounded bg-secondary/10 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{getCategoryLabel(amend.category)}</p>
                        <p className="text-xs text-muted-foreground">{amend.deal_id?.slice(0, 8)}...</p>
                      </div>
                      <Badge variant="outline">{amend.status || 'PENDING'}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!overview.total_amendments && (
            <Card className="glass-card border-white/10">
              <CardContent className="p-8 text-center">
                <Brain className="w-12 h-12 mx-auto mb-4 text-rose-400 opacity-50" />
                <p className="text-muted-foreground">No amendments recorded yet</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Amendment Intelligence will analyze and provide recommendations as amendments are proposed.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Patterns Tab */}
      {activeTab === 'patterns' && patterns && (
        <div className="space-y-6">
          {/* Pattern Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-rose-400">{patterns.total_amendments_analyzed}</div>
                <p className="text-xs text-muted-foreground">Amendments Analyzed</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-green-400">{patterns.average_success_rate}%</div>
                <p className="text-xs text-muted-foreground">Avg Success Rate</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-red-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-display text-red-400">{getCategoryLabel(patterns.highest_risk_category) || 'N/A'}</div>
                <p className="text-xs text-muted-foreground">Highest Risk Category</p>
              </CardContent>
            </Card>
          </div>

          {/* Patterns by Category */}
          {patterns.patterns?.length > 0 ? (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-rose-400" />
                  Amendment Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {patterns.patterns.map((p, idx) => (
                    <div key={idx} className="p-4 rounded bg-secondary/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-rose-400">{getCategoryLabel(p.category)}</span>
                        <Badge variant="secondary">{p.total_occurrences} occurrences</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-center text-sm">
                        <div>
                          <p className="text-lg font-display text-green-400">{(p.success_rate * 100).toFixed(0)}%</p>
                          <p className="text-xs text-muted-foreground">Success Rate</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-yellow-400">{p.avg_delay_caused?.toFixed(0) || 0}</p>
                          <p className="text-xs text-muted-foreground">Avg Delay (days)</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-primary">{p.avg_price_impact?.toFixed(1) || 0}%</p>
                          <p className="text-xs text-muted-foreground">Avg Price Impact</p>
                        </div>
                        <div>
                          <p className="text-lg font-display text-orange-400">{p.risk_factors?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Risk Factors</p>
                        </div>
                      </div>
                      {p.risk_factors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-xs text-muted-foreground mb-2">Risk Factors:</p>
                          <div className="flex flex-wrap gap-2">
                            {p.risk_factors.map((rf, ridx) => (
                              <Badge key={ridx} variant="outline" className="text-xs text-orange-400 border-orange-600/30">
                                {rf}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="glass-card border-white/10">
              <CardContent className="p-8 text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-rose-400 opacity-50" />
                <p className="text-muted-foreground">Insufficient data for pattern analysis</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Patterns will emerge as more amendments are processed.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Insights */}
          {patterns.insights?.length > 0 && (
            <Card className="glass-card border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {patterns.insights.map((insight, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Simulate Tab */}
      {activeTab === 'simulate' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Simulation Inputs */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileEdit className="w-5 h-5 text-rose-400" />
                Simulation Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Amendment Category</Label>
                <select
                  value={simCategory}
                  onChange={(e) => setSimCategory(e.target.value)}
                  className="w-full p-2 bg-secondary/20 border border-white/10 rounded text-foreground"
                  data-testid="sim-category-select"
                >
                  <option value="PRICE_ADJUSTMENT">Price Adjustment</option>
                  <option value="TIMELINE_EXTENSION">Timeline Extension</option>
                  <option value="PAYMENT_RESTRUCTURE">Payment Restructure</option>
                  <option value="CONDITION_MODIFICATION">Condition Modification</option>
                  <option value="PARTY_SUBSTITUTION">Party Substitution</option>
                  <option value="SCOPE_CHANGE">Scope Change</option>
                </select>
              </div>
              
              <div>
                <Label>Price Change (%)</Label>
                <Input
                  type="number"
                  value={simPriceChange}
                  onChange={(e) => setSimPriceChange(Number(e.target.value))}
                  className="bg-secondary/20 border-white/10"
                  placeholder="-5 for 5% reduction"
                  data-testid="sim-price-change"
                />
              </div>
              
              <div>
                <Label>Extension Days</Label>
                <Input
                  type="number"
                  value={simExtensionDays}
                  onChange={(e) => setSimExtensionDays(Number(e.target.value))}
                  className="bg-secondary/20 border-white/10"
                  min="1"
                  max="90"
                  data-testid="sim-extension-days"
                />
              </div>
              
              <div>
                <Label>Deal Stage</Label>
                <select
                  value={simDealStage}
                  onChange={(e) => setSimDealStage(e.target.value)}
                  className="w-full p-2 bg-secondary/20 border border-white/10 rounded text-foreground"
                  data-testid="sim-deal-stage"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="NEAR_COMPLETION">Near Completion</option>
                  <option value="FINAL_STAGE">Final Stage</option>
                </select>
              </div>
              
              <div>
                <Label>Existing Amendments</Label>
                <Input
                  type="number"
                  value={simExistingAmendments}
                  onChange={(e) => setSimExistingAmendments(Number(e.target.value))}
                  className="bg-secondary/20 border-white/10"
                  min="0"
                  max="10"
                  data-testid="sim-existing-amendments"
                />
              </div>
              
              <Button onClick={runSimulation} className="w-full gap-2" data-testid="run-simulation-btn">
                <Zap className="w-4 h-4" /> Analyze Amendment
              </Button>
            </CardContent>
          </Card>

          {/* Simulation Results */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-rose-400" />
                AI Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {simulation ? (
                <div className="space-y-4">
                  {/* Recommendation */}
                  <div className={`p-4 rounded-lg text-center ${getRecommendationColor(simulation.analysis?.recommendation)}`}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      {getRecommendationIcon(simulation.analysis?.recommendation)}
                      <span className="text-xl font-display">
                        {simulation.analysis?.recommendation?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  
                  {/* Risk Metrics */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <p className={`text-lg font-display ${getRiskColor(simulation.analysis?.risk_level)}`}>
                        {simulation.analysis?.risk_level}
                      </p>
                      <p className="text-xs text-muted-foreground">Risk Level</p>
                    </div>
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <p className="text-lg font-display text-yellow-400">
                        {simulation.analysis?.completion_impact}
                      </p>
                      <p className="text-xs text-muted-foreground">Completion Impact</p>
                    </div>
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <p className="text-lg font-display text-orange-400">
                        {simulation.analysis?.delay_impact}
                      </p>
                      <p className="text-xs text-muted-foreground">Delay Impact</p>
                    </div>
                    <div className="p-3 rounded bg-secondary/10 text-center">
                      <p className="text-lg font-display text-red-400">
                        {simulation.analysis?.dispute_probability}
                      </p>
                      <p className="text-xs text-muted-foreground">Dispute Risk</p>
                    </div>
                  </div>
                  
                  {/* Explanation */}
                  {simulation.explanation && (
                    <div className="p-3 rounded bg-primary/10 border border-primary/20">
                      <p className="text-xs text-muted-foreground flex items-start gap-2">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
                        {simulation.explanation}
                      </p>
                    </div>
                  )}
                  
                  {/* Suggested Actions */}
                  {simulation.suggested_actions?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Suggested Actions</p>
                      <ul className="space-y-1">
                        {simulation.suggested_actions.map((action, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="w-8 h-8 mx-auto mb-4 opacity-50" />
                  <p>Configure amendment parameters</p>
                  <p className="text-xs mt-2">and click Analyze to see AI recommendations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AmendmentIntelligencePanel;
